import React from 'react';
import {
  APLICAR_A_MES_MENSUAL_OPTIONS,
  APLICAR_A_MES_SEMESTRAL_OPTIONS,
  MES_CALENDARIO_OPTIONS,
  type PeriodoActividadFormValues,
  resetPeriodoActividadDataFields,
} from '../../shared/profase-periodo-actividad';
import { formatWorkflowPeriodicidadLabel, normalizeWorkflowPeriodicidad, TIPO_PERIODO_OPTIONS } from '../../shared/workflow-periodicidad';

const fieldStyles: Record<string, React.CSSProperties> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--crm-text-secondary)',
  },
  fieldHint: {
    fontSize: 11,
    color: 'var(--crm-text-muted)',
  },
  fieldError: {
    fontSize: 11,
    color: '#dc2626',
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
  },
  radioRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    fontSize: 13,
    color: 'var(--crm-body-text)',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
};

interface PeriodoActividadFormFieldsProps {
  form: PeriodoActividadFormValues;
  errors: Partial<Record<keyof PeriodoActividadFormValues, string>>;
  disabled?: boolean;
  workflowTipoPeriodo?: string | null;
  onChange: (next: PeriodoActividadFormValues) => void;
  onClearError: (key: keyof PeriodoActividadFormValues) => void;
}

export default function PeriodoActividadFormFields({
  form,
  errors,
  disabled = false,
  workflowTipoPeriodo,
  onChange,
  onClearError,
}: PeriodoActividadFormFieldsProps) {
  const tipo = normalizeWorkflowPeriodicidad(form.tipoPeriodo);
  const workflowTipo = normalizeWorkflowPeriodicidad(workflowTipoPeriodo);

  return (
    <div style={fieldStyles.grid}>
      <label style={fieldStyles.field}>
        <span style={fieldStyles.fieldLabel}>Nombre *</span>
        <input
          type="text"
          value={form.nombre}
          onChange={e => {
            onChange({ ...form, nombre: e.target.value });
            onClearError('nombre');
          }}
          placeholder="Ej: Actividades mensuales"
          style={fieldStyles.input}
          disabled={disabled}
        />
        {errors.nombre ? <span style={fieldStyles.fieldError}>{errors.nombre}</span> : null}
      </label>

      <label style={fieldStyles.field}>
        <span style={fieldStyles.fieldLabel}>Tipo periodo *</span>
        <select
          value={form.tipoPeriodo}
          onChange={e => {
            onChange(resetPeriodoActividadDataFields(form, e.target.value));
            onClearError('tipoPeriodo');
          }}
          style={fieldStyles.input}
          disabled={disabled}
        >
          <option value="">Seleccione tipo…</option>
          {TIPO_PERIODO_OPTIONS.map(opt => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.tipoPeriodo ? (
          <span style={fieldStyles.fieldError}>{errors.tipoPeriodo}</span>
        ) : workflowTipo && form.tipoPeriodo && form.tipoPeriodo !== workflowTipo ? (
          <span style={fieldStyles.fieldHint}>
            Distinto a la periodicidad del workflow (
            {formatWorkflowPeriodicidadLabel(workflowTipoPeriodo)}
            ): no podrá asignarse hasta que coincidan.
          </span>
        ) : (
          <span style={fieldStyles.fieldHint}>
            Debe coincidir con la periodicidad del workflow para poder asignarlo.
          </span>
        )}
      </label>

      <label style={fieldStyles.field}>
        <span style={fieldStyles.fieldLabel}>Número periodo</span>
        <input
          type="number"
          value={form.numeroPeriodo}
          onChange={e => {
            onChange({ ...form, numeroPeriodo: e.target.value });
            onClearError('numeroPeriodo');
          }}
          placeholder="Vacío = todos"
          style={fieldStyles.input}
          disabled={disabled}
        />
        {errors.numeroPeriodo ? (
          <span style={fieldStyles.fieldError}>{errors.numeroPeriodo}</span>
        ) : (
          <span style={fieldStyles.fieldHint}>Opcional. Vacío aplica a cualquier número del tipo.</span>
        )}
      </label>

      {tipo === 'MENSUAL' ? (
        <label style={fieldStyles.field}>
          <span style={fieldStyles.fieldLabel}>Aplicar a mes *</span>
          <select
            value={form.aplicarAMes}
            onChange={e => {
              onChange({ ...form, aplicarAMes: e.target.value });
              onClearError('aplicarAMes');
            }}
            style={fieldStyles.input}
            disabled={disabled}
          >
            {APLICAR_A_MES_MENSUAL_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {errors.aplicarAMes ? (
            <span style={fieldStyles.fieldError}>{errors.aplicarAMes}</span>
          ) : (
            <span style={fieldStyles.fieldHint}>Define la fecha de referencia para vencimientos.</span>
          )}
        </label>
      ) : null}

      {tipo === 'ANUAL' ? (
        <label style={fieldStyles.field}>
          <span style={fieldStyles.fieldLabel}>Mes de vencimiento *</span>
          <select
            value={form.mesDeVencimiento}
            onChange={e => {
              onChange({ ...form, mesDeVencimiento: e.target.value });
              onClearError('mesDeVencimiento');
            }}
            style={fieldStyles.input}
            disabled={disabled}
          >
            {MES_CALENDARIO_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {errors.mesDeVencimiento ? (
            <span style={fieldStyles.fieldError}>{errors.mesDeVencimiento}</span>
          ) : (
            <span style={fieldStyles.fieldHint}>Mes del año siguiente al fiscal.</span>
          )}
        </label>
      ) : null}

      {tipo === 'SEMESTRAL' ? (
        <>
          <div style={{ ...fieldStyles.field, ...fieldStyles.fullWidth }}>
            <span style={fieldStyles.fieldLabel}>Modo semestral *</span>
            <div style={fieldStyles.radioRow}>
              <label>
                <input
                  type="radio"
                  name="semestralModo"
                  checked={form.semestralModo === 'offset'}
                  disabled={disabled}
                  onChange={() => {
                    onChange({ ...form, semestralModo: 'offset' });
                    onClearError('aplicarAMes');
                    onClearError('mesDeVencimiento');
                  }}
                />
                {' '}Offset dentro del semestre
              </label>
              <label>
                <input
                  type="radio"
                  name="semestralModo"
                  checked={form.semestralModo === 'calendario'}
                  disabled={disabled}
                  onChange={() => {
                    onChange({ ...form, semestralModo: 'calendario' });
                    onClearError('aplicarAMes');
                    onClearError('mesDeVencimiento');
                  }}
                />
                {' '}Mes calendario fijo
              </label>
            </div>
          </div>
          {form.semestralModo === 'offset' ? (
            <label style={fieldStyles.field}>
              <span style={fieldStyles.fieldLabel}>Aplicar a mes (offset) *</span>
              <select
                value={form.aplicarAMes}
                onChange={e => {
                  onChange({ ...form, aplicarAMes: e.target.value });
                  onClearError('aplicarAMes');
                }}
                style={fieldStyles.input}
                disabled={disabled}
              >
                {APLICAR_A_MES_SEMESTRAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {errors.aplicarAMes ? <span style={fieldStyles.fieldError}>{errors.aplicarAMes}</span> : null}
            </label>
          ) : (
            <label style={fieldStyles.field}>
              <span style={fieldStyles.fieldLabel}>Mes calendario *</span>
              <select
                value={form.mesDeVencimiento}
                onChange={e => {
                  onChange({ ...form, mesDeVencimiento: e.target.value });
                  onClearError('mesDeVencimiento');
                }}
                style={fieldStyles.input}
                disabled={disabled}
              >
                {MES_CALENDARIO_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {errors.mesDeVencimiento ? (
                <span style={fieldStyles.fieldError}>{errors.mesDeVencimiento}</span>
              ) : null}
            </label>
          )}
        </>
      ) : null}

      {tipo === 'OTRO' ? (
        <div style={{ ...fieldStyles.field, ...fieldStyles.fullWidth }}>
          <span style={fieldStyles.fieldHint}>Sin configuración adicional de referencia de vencimiento.</span>
        </div>
      ) : null}

      <label style={{ ...fieldStyles.field, ...fieldStyles.fullWidth }}>
        <span style={fieldStyles.fieldLabel}>Generación automática</span>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--crm-body-text)' }}>
          <input
            type="checkbox"
            checked={form.generarAutomaticamente}
            disabled={disabled}
            onChange={e => onChange({ ...form, generarAutomaticamente: e.target.checked })}
          />
          <span>
            Generar instancias automáticamente
            <span style={{ ...fieldStyles.fieldHint, display: 'block', marginTop: 2 }}>
              Si está activo, el scheduler crea instancias y les asigna generarAutomaticamente = true.
              Esas instancias son las que calendarizan procesos. Después cada instancia se puede apagar
              (Anular o checkbox) sin cambiar esta definición. Desmarcado por defecto.
            </span>
          </span>
        </label>
      </label>
    </div>
  );
}

export { fieldStyles as periodoActividadFormFieldStyles };
